import CircularProgress from "@material-ui/core/CircularProgress";
import { useStylesFacebook } from "./WrappedSecurity.styles";
import { Security } from "@okta/okta-react";
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import React, { createContext, Props, useContext, useEffect, useState } from "react";
import { ConfigContext } from "./ConfigContext";
import { CurrentUserProvider } from "./SecurityContext";
import { UserContextProvider } from "../context/UserContext";
import { useHistory } from "react-router-dom";

export const SecurityContext = createContext<[boolean | null, string[]]>([null, null]);

export default function WrappedSecurity(props: Props<any>) {
  const classes = useStylesFacebook();
  const [config, isConfigLoading] = useContext(ConfigContext);
  const [isSecurityLoaded, setIsSecurityLoaded] = useState(false);
  const [userScopes, setUserScopes] = useState([]);
  const history = useHistory();
  const [oktaAuthObj, setOktaAuthObj] = useState();
  let oktaAuthObjTemp;

  const fetchNewScopesForUser = async ({ isAuthenticated, ..._rest }) => {
    if (document['processingSecurity']) {
      return;
    }
    document['processingSecurity'] = true;
    // get new accessToken with more scopes
    if (isAuthenticated) {
      const idToken = await oktaAuthObjTemp.tokenManager.get("idToken");

      // get "group" claim
      const userGroupList: string[] = idToken.claims?.[config.groupClaimName];

      if (!userGroupList) {
        throw new Error(`${config.groupClaimName} not found in idToken`);
      }

      // get list of Scopes
      const scopes: string[] = extractScopesForUserGroups(userGroupList, config.rbacRoles);

      // save the user's scopes for the <Can> component
      setUserScopes(scopes);

      try {
        // eslint-disable-next-line
        const { tokens } = await oktaAuthObjTemp.token.getWithoutPrompt({
          scopes,
          responseType: ["token"],
          pkce: false
        });
        oktaAuthObjTemp.tokenManager.add("accessToken", tokens.accessToken);
        setIsSecurityLoaded(true);
      } catch (error) {
        if (error?.errorCode === "login_required") {
          // Logout
          oktaAuthObjTemp.signOut();
        } else {
          alert("Please enable '3rd Party Cookies' in your Browser Settings. Then reload the window.");
        }
      }
      finally {
        oktaAuthObjTemp.authStateManager.unsubscribe(fetchNewScopesForUser);
      }
    } else {
      // not Authenticated
      // setUserScopes([]);
      setIsSecurityLoaded(false);
      oktaAuthObjTemp.authStateManager.unsubscribe(fetchNewScopesForUser);
    }
  };

  // update localStorage to keep the flag during page reloads
  useEffect(() => {
    localStorage.setItem("isSecurityLoaded", isSecurityLoaded ? "true" : "false");
  }, [isSecurityLoaded]);

  useEffect(() => {
    if (!isConfigLoading) {
      const oktaAuth = new OktaAuth(config?.oidc);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      oktaAuthObjTemp = oktaAuth;
      setOktaAuthObj(oktaAuth as any);
      oktaAuth.authStateManager.subscribe(fetchNewScopesForUser.bind(oktaAuth));
    }
  }, [isConfigLoading]);

  // Show loading spinner
  if (isConfigLoading || !oktaAuthObj) {
    return (
      <div className={classes.root}>
        <CircularProgress
          variant="indeterminate"
          disableShrink
          className={classes.top}
          classes={{
            circle: classes.circle,
          }}
          size={40}
          thickness={4}></CircularProgress>
      </div>
    );
  } else {
    const restoreOriginalUri = async (_oktaAuth, originalUri) => {
      history.replace(toRelativeUrl((originalUri || '/'), window.location.origin));
    };

    return (
      <SecurityContext.Provider value={[isSecurityLoaded, userScopes]}>
        <Security oktaAuth={oktaAuthObj} restoreOriginalUri={restoreOriginalUri}>
          <CurrentUserProvider>
              <UserContextProvider>      
                {props.children} 
              </UserContextProvider>
          </CurrentUserProvider>
        </Security>
      </SecurityContext.Provider>
    );
  }
}

/**
 * get the list of scopes for the selected groups
 *
 * @param userGroups
 * @param rbacRoles
 */
function extractScopesForUserGroups(userGroups: string[], rbacRoles: any[]): string[] {
  let scopes = (rbacRoles.find((obj) => obj.group === "*") || { scopes: [] }).scopes;

  // loop on each AD group
  userGroups &&
    userGroups.forEach((group) => {
      // get the scopes
      const roleObj = rbacRoles.find((role) => role.group === group);
      if (roleObj) {
        roleObj.scopes.forEach((scope) => (!scopes.includes(scope) ? scopes.push(scope) : null));
      }
      return group;
    });

  return scopes;
}
